import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {OfficeBuildingIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {PlusIcon} from "@heroicons/react/solid";
import {Site} from "types/Site";
import {useNavigate} from "react-router-dom";
import DeleteSite from '../Delete';
import {useOperator} from "context/OperatorContext";
import {useAuth} from "../../../context/AuthContext";
import SiteContextMenu from "../../../components/menus/SiteContextMenu";
import NotAuthorised from "../../NotAuthorised";

// Component ----

function SiteList() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const navigate = useNavigate();

  const [deletingSite, setDeletingSite] = useState<Site | null>(null);
  const [page, setPage] = useState<number>(1);

  const result = useQuery(
    ['sites.all', {page}],
    () => ngage.sites.all(page),
    {keepPreviousData: true, enabled: auth.can('sites:read')}
  )

  // Prevent unauthorised viewing
  if (auth.cannot('sites:read')) {
    return <NotAuthorised/>
  }

  // Prefetch next and previous pages
  prefetchNearbyPages('sites.all', page, ngage.sites.all)

  // Prepare page actions
  const actions = (
    <>
      {auth.can('sites:write') &&
        <Button icon={PlusIcon} onClick={() => navigate('/sites/new')}>New</Button>
      }
    </>
  )

  // Prepare breadcrumbs
  const breadcrumbs = {
    'Operations': '/', [termFor('Sites')]: '/sites'
  }

  return (
    <Page
      title={termFor('Sites')}
      actions={actions}
      breadcrumbs={breadcrumbs}
    >
      <Table paginationData={result.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Address</TableHeader>
            <TableHeader>Email</TableHeader>
            <TableHeader>Phone</TableHeader>
            <TableHeader>Added</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={result.isLoading}>

          {result?.data && result.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={6}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <OfficeBuildingIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No {termFor('sites')}</span>
                </div>
              </TableData>
            </TableRow>
            : result?.data?.data.map((site, index) => (
              <TableRow key={site.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <SiteContextMenu
                        site={site}
                        onDeleteSite={() => setDeletingSite(site)}
                        deleteDisabled={Number(result?.data?.total) < 2}
                        position="left"
                      />
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <OfficeBuildingIcon className="h-5 w-5 mr-2 text-gray-500"/> {site.name}
                    </div>
                  </div>
                </TableData>
                <TableData>{site?.address?.line1} {site?.address?.city}</TableData>
                <TableData>
                  {site.email ?
                    <a href={`mailto:${site.email}`}
                       className="text-brand border-b border-dashed border-brand">{site.email}</a>
                    : null}
                </TableData>
                <TableData>
                  {site.phone ?
                    <a href={`tel:${site.phone}`}
                       className="text-brand border-b border-dashed border-brand">{site.phone}</a>
                    : null}
                </TableData>
                <TableData>{formattedDateTime(site.created_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <SiteContextMenu
                    site={site}
                    onDeleteSite={() => setDeletingSite(site)}
                    deleteDisabled={Number(result?.data?.total) < 2}
                  />
                </TableData>

                {/* Delete confirmation modal */}
                <DeleteSite
                  site={deletingSite}
                  show={site === deletingSite}
                  onCancel={() => setDeletingSite(null)}
                  onDelete={() => navigate('/sites')}
                  onDeleteFail={() => setDeletingSite(null)}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
    </Page>
  );
}

export default SiteList;